import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { QuizBuilder, type QuizBuilderData } from '@/components/quiz-builder/QuizBuilder';
import { useQuizlet, useUpdateQuizlet } from '@/queries/quizlets';
import type { NewQuestion } from '@/types';

import { BackgroundWaves } from '@/components/BackgroundWaves';
import styles from './quiz-page.module.scss';

export const Route = createFileRoute('/quizlets/$quizletId/edit')({
  component: EditQuizlet,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function EditQuizlet() {
  const { quizletId } = Route.useParams();
  const navigate = useNavigate();

  // Load existing quizlet
  const { data: quizlet, isLoading, error } = useQuizlet(quizletId);

  // Update mutation
  const updateQuizlet = useUpdateQuizlet();

  const handleSave = async (data: QuizBuilderData) => {
    try {
      await updateQuizlet.mutateAsync({
        id: quizletId,
        data: {
          name: data.name,
          is_daily_knowledge: data.is_daily_knowledge,
        },
      });

      // Navigate back to quizlet detail
      navigate({
        to: '/quizlets/$quizletId',
        params: { quizletId },
      });
    } catch (error) {
      console.error('Failed to save quizlet:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    // Show confirmation if there are unsaved changes
    const confirmed = window.confirm(
      'Are you sure you want to discard your changes?'
    );

    if (confirmed) {
      navigate({
        to: '/quizlets/$quizletId',
        params: { quizletId },
      });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.quizPage}>
        <BackgroundWaves />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error || !quizlet) {
    return (
      <div className={styles.quizPage}>
        <BackgroundWaves />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Failed to load quiz. Please try again.</p>
          <button
            onClick={() =>
              navigate({ to: '/quizlets/$quizletId', params: { quizletId } })
            }
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Transform quizlet data to QuizBuilderData format
  const initialData = {
    name: quizlet.name,
    original_prompt: quizlet.original_prompt,
    is_daily_knowledge: quizlet.is_daily_knowledge,
    questions: (quizlet.questions || []).map((q): NewQuestion => ({
      question_text: q.question_text,
      question_type: q.question_type,
      content: q.content,
      explanation: q.explanation,
      difficulty: q.difficulty,
      order_index: q.order_index,
      tags: q.tags || [],
    })),
  };

  return (
    <div className={styles.quizPage}>
      <BackgroundWaves />

      {/* Breadcrumb Navigation */}
      <div className={styles.quizPage__breadcrumb}>
        <nav className={styles.quizPage__nav}>
          <a
            href="/quizlets"
            className={styles.quizPage__link}
            onClick={(e) => {
              e.preventDefault();
              navigate({ to: '/quizlets' });
            }}
          >
            Quizlets
          </a>
          <span className={styles.quizPage__separator}>›</span>
          <a
            href={`/quizlets/${quizletId}`}
            className={styles.quizPage__link}
            onClick={(e) => {
              e.preventDefault();
              navigate({ to: '/quizlets/$quizletId', params: { quizletId } });
            }}
          >
            {quizlet.name}
          </a>
          <span className={styles.quizPage__separator}>›</span>
          <span className={styles.quizPage__current}>Edit</span>
        </nav>
      </div>

      <QuizBuilder
        existingQuizletId={quizletId}
        initialData={initialData}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
}
